import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../../core/services/api.service';
import { DocumentConfig, EnergyTypeOptionsResponse, Loa, LoaBillConfig, LoaEmd, LoaSchedule } from '../../../core/models/domain.models';

@Injectable({ providedIn: 'root' })
export class LoaService {
  private readonly api = inject(ApiService);

  list() {
    return this.api.get<Loa[]>('/api/loas');
  }

  listEnergyTypes() {
    return this.api.get<EnergyTypeOptionsResponse>('/api/loa-energy-types');
  }

  get(id: string) {
    return this.api.get<Loa>(`/api/loas/${id}`);
  }

  create(payload: Partial<Loa>) {
    return this.api.post<Loa>('/api/loas', payload);
  }

  createWorkflow(payload: Partial<Loa>) {
    return this.api.post<Loa>('/api/loa/create', payload);
  }

  update(id: string, payload: Partial<Loa>) {
    return this.api.patch<Loa>(`/api/loas/${id}`, payload);
  }

  initialApprove(id: string, comments?: string) {
    return this.api.post<Loa>(`/api/loa/${id}/initial-approve`, { comments: comments || null });
  }

  initialReject(id: string, comments?: string) {
    return this.api.post<Loa>(`/api/loa/${id}/initial-reject`, { comments: comments || null });
  }

  finalApprove(id: string, comments?: string) {
    return this.api.post<Loa>(`/api/loa/${id}/final-approve`, { comments: comments || null });
  }

  finalReject(id: string, comments?: string) {
    return this.api.post<Loa>(`/api/loa/${id}/final-reject`, { comments: comments || null });
  }

  accountsApprove(id: string, comments?: string) {
    return this.api.post<Loa>(`/api/loa/${id}/accounts-approve`, { comments: comments || null });
  }

  accountsReject(id: string, comments?: string) {
    return this.api.post<Loa>(`/api/loa/${id}/accounts-reject`, { comments: comments || null });
  }

  operationApprove(id: string, comments?: string) {
    return this.api.post<Loa>(`/api/loa/${id}/operation-approve`, { comments: comments || null });
  }

  operationReject(id: string, comments?: string) {
    return this.api.post<Loa>(`/api/loa/${id}/operation-reject`, { comments: comments || null });
  }

  delete(id: string) {
    return this.api.delete<{ message: string }>(`/api/loas/${id}`);
  }

  listSchedules(loaId: string) {
    return this.api.get<LoaSchedule[]>(`/api/loas/${loaId}/schedules`);
  }

  createSchedule(loaId: string, payload: Partial<LoaSchedule>) {
    return this.api.post<LoaSchedule>(`/api/loas/${loaId}/schedules`, payload);
  }

  updateSchedule(id: string, payload: Partial<LoaSchedule>) {
    return this.api.patch<LoaSchedule>(`/api/loa-schedules/${id}`, payload);
  }

  deleteSchedule(id: string) {
    return this.api.delete<{ message: string }>(`/api/loa-schedules/${id}`);
  }

  listEmds(loaId: string) {
    return this.api.get<LoaEmd[]>(`/api/loas/${loaId}/emds`);
  }

  createEmd(loaId: string, payload: Partial<LoaEmd>) {
    return this.api.post<LoaEmd>(`/api/loas/${loaId}/emds`, payload);
  }

  updateEmd(id: string, payload: Partial<LoaEmd>) {
    return this.api.patch<LoaEmd>(`/api/loa-emds/${id}`, payload);
  }

  deleteEmd(id: string) {
    return this.api.delete<{ message: string }>(`/api/loa-emds/${id}`);
  }

  listBillConfigs(loaId: string) {
    return this.api.get<LoaBillConfig[]>(`/api/loas/${loaId}/bill-configs`);
  }

  createBillConfig(loaId: string, payload: Partial<LoaBillConfig>) {
    return this.api.post<LoaBillConfig>(`/api/loas/${loaId}/bill-configs`, payload);
  }

  updateBillConfig(id: string, payload: Partial<LoaBillConfig>) {
    return this.api.patch<LoaBillConfig>(`/api/loa-bill-configs/${id}`, payload);
  }

  deleteBillConfig(id: string) {
    return this.api.delete<{ message: string }>(`/api/loa-bill-configs/${id}`);
  }

  listDocumentConfigs() {
    return this.api.get<DocumentConfig[]>('/api/document-configs');
  }

  createDocumentConfig(payload: Partial<DocumentConfig>) {
    return this.api.post<DocumentConfig>('/api/document-configs', payload);
  }

  updateDocumentConfig(id: string, payload: Partial<DocumentConfig>) {
    return this.api.patch<DocumentConfig>(`/api/document-configs/${id}`, payload);
  }

  deleteDocumentConfig(id: string) {
    return this.api.delete<{ message: string }>(`/api/document-configs/${id}`);
  }
}
